'use client';
import React, { useState, useCallback } from 'react';
import { Search, MapPin, UtensilsCrossed, Star, ArrowRight, ChevronRight, Heart, Truck, Tag } from 'lucide-react';
import Link from 'next/link';
import AppLayout from '@/components/AppLayout';

interface FoodItem {
  id: string;name: string;restaurant: string;category: string;location: string;
  price: number;rating: number;reviews: number;image_url: string;is_featured: boolean;
  delivery_time: string;delivery_fee: number;tags: string[];is_available: boolean;
}

const FOOD_ITEMS: FoodItem[] = [
{ id: '1', name: 'Nyama Choma Platter (1kg)', restaurant: 'Carnivore Nairobi', category: 'Grills', location: 'Nairobi', price: 2500, rating: 4.9, reviews: 342, image_url: "https://img.rocket.new/generatedImages/rocket_gen_img_13c7182b8-1772948454306.png", is_featured: true, delivery_time: '30–45 min', delivery_fee: 150, tags: ['Grilled', 'Beef', 'Popular'], is_available: true },
{ id: '2', name: 'Pilau Rice & Kachumbari', restaurant: 'Mama Oliech', category: 'Local', location: 'Kisumu', price: 450, rating: 4.8, reviews: 189, image_url: 'https://images.unsplash.com/photo-1512058564366-18510be2db19', is_featured: false, delivery_time: '20–35 min', delivery_fee: 100, tags: ['Pilau', 'Local', 'Spicy'], is_available: true },
{ id: '3', name: 'Chicken Biryani (Full)', restaurant: 'Swahili Plate', category: 'Indian', location: 'Mombasa', price: 750, rating: 4.7, reviews: 256, image_url: 'https://images.unsplash.com/photo-1563379091339-03b21ab4a4f8', is_featured: true, delivery_time: '25–40 min', delivery_fee: 120, tags: ['Biryani', 'Chicken', 'Spicy'], is_available: true },
{ id: '4', name: 'Ugali & Sukuma Wiki', restaurant: 'Githeri House', category: 'Local', location: 'Nairobi', price: 200, rating: 4.5, reviews: 98, image_url: 'https://images.unsplash.com/photo-1512058564366-18510be2db19', is_featured: false, delivery_time: '15–25 min', delivery_fee: 80, tags: ['Ugali', 'Vegetarian', 'Budget'], is_available: true },
{ id: '5', name: 'Margherita Pizza (12")', restaurant: 'Pizza Inn Kenya', category: 'Pizza', location: 'Nairobi', price: 1200, rating: 4.4, reviews: 445, image_url: 'https://images.unsplash.com/photo-1565299624946-b28f40a0ae38', is_featured: false, delivery_time: '35–50 min', delivery_fee: 200, tags: ['Pizza', 'Italian', 'Cheese'], is_available: true },
{ id: '6', name: 'Samosa Platter (10 pcs)', restaurant: 'Zam Zam', category: 'Snacks', location: 'Mombasa', price: 350, rating: 4.6, reviews: 167, image_url: 'https://images.unsplash.com/photo-1601050690597-df0568f70950', is_featured: false, delivery_time: '20–30 min', delivery_fee: 100, tags: ['Samosa', 'Snack', 'Halal'], is_available: true }];


const CATEGORIES = ['All', 'Local', 'Grills', 'Indian', 'Pizza', 'Snacks', 'Burgers', 'Seafood'];
const LOCATIONS = ['All Locations', 'Nairobi', 'Mombasa', 'Kisumu', 'Nakuru'];
const SORT_OPTIONS = ['Popular', 'Fastest', 'Cheapest', 'Top Rated'];

export default function FoodLandingPage() {
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('All');
  const [location, setLocation] = useState('All Locations');
  const [sort, setSort] = useState('Popular');
  const [savedIds, setSavedIds] = useState<Set<string>>(new Set());

  const filtered = FOOD_ITEMS.filter((f) => {
    const matchSearch = !search || f.name.toLowerCase().includes(search.toLowerCase()) || f.restaurant.toLowerCase().includes(search.toLowerCase());
    const matchCat = category === 'All' || f.category === category;
    const matchLoc = location === 'All Locations' || f.location === location;
    return matchSearch && matchCat && matchLoc;
  }).sort((a, b) => {
    if (sort === 'Cheapest') return a.price - b.price;
    if (sort === 'Top Rated') return b.rating - a.rating;
    return b.reviews - a.reviews;
  });

  const toggleSave = useCallback((id: string) => {
    setSavedIds((prev) => {const n = new Set(prev);n.has(id) ? n.delete(id) : n.add(id);return n;});
  }, []);

  return (
    <AppLayout>
      <div className="min-h-screen" style={{ background: '#070C1A' }}>
        {/* Hero */}
        <div className="relative px-4 pt-8 pb-10 overflow-hidden"
        style={{ background: 'linear-gradient(135deg, #1a0800 0%, #0d0a00 50%, #070C1A 100%)' }}>
          <div className="absolute inset-0 opacity-20"
          style={{ backgroundImage: 'radial-gradient(circle at 25% 50%, #FF6D00 0%, transparent 50%), radial-gradient(circle at 75% 30%, #FFB300 0%, transparent 50%)' }} />
          <div className="relative max-w-2xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-600 mb-4"
            style={{ background: 'rgba(255,109,0,0.15)', color: '#FF6D00', border: '1px solid rgba(255,109,0,0.3)' }}>
              <UtensilsCrossed size={12} /> {1850 .toLocaleString()} Restaurants & Dishes
            </div>
            <h1 className="text-3xl font-800 text-white mb-3 leading-tight">
              Order <span style={{ color: '#FF6D00' }}>Delicious Food</span> Delivered
            </h1>
            <p className="text-sm mb-6" style={{ color: 'rgba(255,255,255,0.55)' }}>
              Local favourites, grills, pizza and more. Fast delivery across Kenya.
            </p>
            <div className="flex gap-2 max-w-lg mx-auto">
              <div className="relative flex-1">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'rgba(255,255,255,0.4)' }} />
                <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search food or restaurant…"
                className="w-full pl-9 pr-3 py-3.5 rounded-2xl text-sm outline-none"
                style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)', color: '#fff' }} />
              </div>
              <div className="relative">
                <MapPin size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'rgba(255,255,255,0.4)' }} />
                <select value={location} onChange={(e) => setLocation(e.target.value)}
                className="pl-8 pr-3 py-3.5 rounded-2xl text-sm outline-none appearance-none"
                style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)', color: '#fff' }}>
                  {LOCATIONS.map((l) => <option key={l} value={l} style={{ background: '#0D1526' }}>{l}</option>)}
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Category pills */}
        <div className="px-4 py-3 sticky top-0 z-20"
        style={{ background: 'rgba(7,12,26,0.95)', backdropFilter: 'blur(12px)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
          <div className="max-w-4xl mx-auto flex items-center gap-2 overflow-x-auto pb-1">
            {CATEGORIES.map((c) =>
            <button key={c} onClick={() => setCategory(c)}
            className="px-3 py-2 rounded-xl text-xs font-600 flex-shrink-0 transition-all"
            style={{ background: category === c ? 'rgba(255,109,0,0.15)' : 'rgba(255,255,255,0.04)', color: category === c ? '#FF6D00' : 'rgba(255,255,255,0.55)', border: `1px solid ${category === c ? 'rgba(255,109,0,0.3)' : 'rgba(255,255,255,0.08)'}` }}>
                {c}
              </button>
            )}
            <div className="flex-shrink-0 w-px h-5 mx-1" style={{ background: 'rgba(255,255,255,0.1)' }} />
            {SORT_OPTIONS.map((s) =>
            <button key={s} onClick={() => setSort(s)}
            className="px-3 py-2 rounded-xl text-xs font-600 flex-shrink-0 transition-all"
            style={{ background: sort === s ? 'rgba(255,179,0,0.15)' : 'rgba(255,255,255,0.04)', color: sort === s ? '#FFB300' : 'rgba(255,255,255,0.55)', border: `1px solid ${sort === s ? 'rgba(255,179,0,0.3)' : 'rgba(255,255,255,0.08)'}` }}>
                {s}
              </button>
            )}
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-4 pt-4 pb-8">
          <div className="flex items-center justify-between mb-4">
            <span className="text-xs" style={{ color: 'rgba(255,255,255,0.4)' }}>
              <span style={{ color: '#FF6D00', fontWeight: 700 }}>{filtered.length}</span> items found
            </span>
            <Link href="/login" className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-700"
            style={{ background: '#FF6D00', color: '#fff' }}>
              Add Your Restaurant <ArrowRight size={12} />
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {filtered.map((item) =>
            <div key={item.id} className="rounded-2xl overflow-hidden"
            style={{ background: 'rgba(255,255,255,0.03)', border: `1px solid ${item.is_featured ? 'rgba(255,109,0,0.25)' : 'rgba(255,255,255,0.07)'}` }}>
                <div className="relative h-40">
                  <img src={item.image_url} alt={`${item.name} from ${item.restaurant} in ${item.location}`}
                className="w-full h-full object-cover" loading="lazy" />
                  {item.is_featured &&
                <span className="absolute top-2 left-2 text-[9px] px-1.5 py-0.5 rounded-md font-700"
                style={{ background: '#FF6D00', color: '#fff' }}>Featured</span>
                }
                  <button onClick={() => toggleSave(item.id)}
                className="absolute top-2 right-2 w-8 h-8 rounded-full flex items-center justify-center"
                style={{ background: savedIds.has(item.id) ? '#FF4081' : 'rgba(0,0,0,0.5)' }}>
                    <Heart size={13} fill={savedIds.has(item.id) ? '#fff' : 'none'} style={{ color: '#fff' }} />
                  </button>
                  <div className="absolute bottom-2 left-2 flex items-center gap-1 px-2 py-1 rounded-lg"
                style={{ background: 'rgba(0,0,0,0.7)' }}>
                    <Truck size={10} style={{ color: '#FF6D00' }} />
                    <span className="text-[10px] font-600" style={{ color: '#FF6D00' }}>{item.delivery_time}</span>
                  </div>
                </div>
                <div className="p-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-700 text-white line-clamp-1">{item.name}</p>
                      <p className="text-xs mt-0.5" style={{ color: 'rgba(255,255,255,0.5)' }}>{item.restaurant}</p>
                    </div>
                    <p className="text-sm font-800 flex-shrink-0" style={{ color: '#FF6D00' }}>KES {item.price.toLocaleString()}</p>
                  </div>
                  <div className="flex items-center gap-3 mt-2">
                    <div className="flex items-center gap-1">
                      <Star size={10} fill="#FFB300" style={{ color: '#FFB300' }} />
                      <span className="text-xs font-700" style={{ color: '#FFB300' }}>{item.rating}</span>
                      <span className="text-[10px]" style={{ color: 'rgba(255,255,255,0.35)' }}>({item.reviews})</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Tag size={10} style={{ color: 'rgba(255,255,255,0.35)' }} />
                      <span className="text-[10px]" style={{ color: 'rgba(255,255,255,0.45)' }}>Delivery KES {item.delivery_fee}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <MapPin size={10} style={{ color: 'rgba(255,255,255,0.35)' }} />
                      <span className="text-[10px]" style={{ color: 'rgba(255,255,255,0.45)' }}>{item.location}</span>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-1 mt-2">
                    {item.tags.map((t) =>
                  <span key={t} className="text-[9px] px-1.5 py-0.5 rounded-md font-600"
                  style={{ background: 'rgba(255,109,0,0.12)', color: '#FFAB40' }}>{t}</span>
                  )}
                  </div>
                  <Link href="/login" className="mt-3 flex items-center justify-center gap-1 py-2 rounded-xl text-xs font-700"
                style={{ background: 'rgba(255,109,0,0.12)', color: '#FF6D00', border: '1px solid rgba(255,109,0,0.2)' }}>
                    Order Now <ChevronRight size={11} />
                  </Link>
                </div>
              </div>
            )}
          </div>

          <div className="mt-8 p-6 rounded-3xl text-center"
          style={{ background: 'linear-gradient(135deg, rgba(255,109,0,0.1), rgba(255,179,0,0.1))', border: '1px solid rgba(255,109,0,0.2)' }}>
            <h3 className="text-lg font-800 text-white mb-2">See All {1850 .toLocaleString()} Dishes</h3>
            <p className="text-sm mb-4" style={{ color: 'rgba(255,255,255,0.5)' }}>Sign up to order, track delivery, and save your favourite restaurants.</p>
            <div className="flex gap-3 justify-center flex-wrap">
              <Link href="/login" className="inline-flex items-center gap-2 px-6 py-3 rounded-2xl font-700 text-sm"
              style={{ background: '#FF6D00', color: '#fff' }}>
                <UtensilsCrossed size={16} /> Order Food
              </Link>
              <Link href="/login" className="inline-flex items-center gap-2 px-6 py-3 rounded-2xl font-700 text-sm"
              style={{ background: 'rgba(255,255,255,0.08)', color: '#fff', border: '1px solid rgba(255,255,255,0.15)' }}>
                Add Your Restaurant
              </Link>
            </div>
          </div>
        </div>
      </div>
    </AppLayout>);

}